/**
 * WHAT SURVIVED `lib/memberCounts.ts` — and what did not
 *
 * WHAT DIED: `displayMemberCount`, and the `MemberCounts` shape it took.
 *
 *   Math.max(g.member_count, g.source_member_count ?? 0)
 *
 * Imported groups got only their admins as live members, so the legacy wire
 * carried TWO figures and every screen had to know which one to believe. The
 * v1 `Group` carries ONE `memberCount`, and since OSO-401 it counts the people
 * who are actually in the group — the Facebook import is not a member count
 * and no longer stands in for one. `?sort=largest` orders by that same number
 * (G1), so the list order and the label on each card cannot disagree.
 * `memberCountBasis` is deprecated and always `"live"`; nothing reads it.
 *
 * WHAT SURVIVED: the Danish rule that one member is a "medlem" and every other
 * number is "medlemmer", and the `da-DK` thousands separator. Those are not
 * the API's shape being undone — no field on the wire carries the sentence —
 * so they stay, in one place, rather than being written out at each of the
 * eight labels that need them.
 */

/** `1234` -> `"1.234"`. */
export function formatCount(n: number): string {
  return n.toLocaleString("da-DK");
}

export function memberWord(n: number): string {
  return n === 1 ? "medlem" : "medlemmer";
}

/** `1234` -> `"1.234 medlemmer"`. */
export function memberCountLabel(n: number): string {
  return `${formatCount(n)} ${memberWord(n)}`;
}

/**
 * The same rule, for the other three quantities the batch's screens print.
 *
 * They were spelled out at the call site with a hardcoded plural — "1
 * medlemmer" on the OG card and the group header, "1 interesserede" on the
 * event card and the event page, "1 undergrupper" in the statistik headline —
 * beside imports of `formatCount` from this very file. A count the server
 * knows should not be spelled two different ways depending on which screen is
 * showing it.
 */
function attendeeWord(n: number): string {
  return n === 1 ? "deltager" : "deltagere";
}

function interestedWord(n: number): string {
  return n === 1 ? "interesseret" : "interesserede";
}

export function subGroupWord(n: number): string {
  return n === 1 ? "undergruppe" : "undergrupper";
}

/** `1` -> `"1 interesseret"`. */
export function interestedCountLabel(n: number): string {
  return `${formatCount(n)} ${interestedWord(n)}`;
}

/** `1` -> `"1 deltager"`. */
export function attendeeCountLabel(n: number): string {
  return `${formatCount(n)} ${attendeeWord(n)}`;
}
